/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import React from "react"
import { BaseButton, Icon } from "@cd2/components"
import { Link } from "react-router-dom"
import { IComponentProp } from "../types/utils"

export type IIcon = string | { icon: string; prefix: IComponentProp<typeof Icon, "prefix"> }

interface ICGButtonProps {
  children: React.ReactNode
  leftIcon?: IIcon
  rightIcon?: IIcon
  onClick?(e: any): void
  buttonStyle?: "outline" | "text" | "solid"
  buttonProps?: any
  extendTheme?: IComponentProp<typeof BaseButton, "theme">
  disabled?: boolean
  buttonColor?: string | { color: string; hover: string }
  to?: string
  openInNewTab?: boolean
  block?: boolean
  className?: string
}

export function CGButton({
  children,
  onClick,
  leftIcon,
  rightIcon,
  buttonStyle = "solid",
  buttonProps,
  extendTheme,
  disabled,
  buttonColor = "primary",
  to,
  openInNewTab,
  block,
  className,
}: ICGButtonProps) {
  function renderIcon(icon?: IIcon) {
    if (!icon) {
      return undefined
    } else if (typeof icon === "string") {
      return <Icon icon={icon} />
    } else {
      return <Icon icon={icon.icon} prefix={icon.prefix} />
    }
  }

  const button = (
    <BaseButton
      buttonStyle={buttonStyle}
      theme={{
        padding: 11,
        fontSize: 14,
        buttonColor: typeof buttonColor === "string" ? buttonColor : buttonColor.color,
        hoverColor: typeof buttonColor === "string" ? undefined : buttonColor.hover,
        ...extendTheme,
      }}
      renderLeft={() => renderIcon(leftIcon)}
      renderRight={() => renderIcon(rightIcon)}
      onClick={onClick}
      disabled={disabled}
      block={block}
      type="button"
      className={className}
      {...buttonProps}
    >
      {children}
    </BaseButton>
  )

  if (to && to.startsWith("http")) {
    return (
      <a
        css={
          buttonStyle === "text"
            ? undefined
            : css`
                text-decoration: none;
              `
        }
        className={className}
        href={to}
        target={openInNewTab ? "_blank" : undefined}
        rel="noopener noreferrer"
      >
        {button}
      </a>
    )
  }

  if (to) {
    return (
      <Link
        css={
          buttonStyle === "text"
            ? undefined
            : css`
                text-decoration: none;
              `
        }
        className={className}
        to={to}
        target={openInNewTab ? "_blank" : undefined}
      >
        {button}
      </Link>
    )
  }

  return button
}
